package com.evoapps.evoshift.ui.legal

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.selection.toggleable
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.Insights
import androidx.compose.material3.Checkbox
import androidx.compose.material3.CheckboxDefaults
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Switch
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.semantics.Role
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.ui.DarkGold
import com.evoapps.evoshift.ui.DeepNavy
import com.evoapps.evoshift.viewmodels.PrivacyChoicesViewModel
import com.evoapps.evoshift.widgets.DialogAction
import com.evoapps.evoshift.widgets.EvoBottomSheet
import com.evoapps.evoshift.widgets.EvoSecondaryButton
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.legal_privacy
import evoshift.composeapp.generated.resources.legal_privacy_url
import evoshift.composeapp.generated.resources.privacy_allow_selected
import evoshift.composeapp.generated.resources.privacy_choices_note
import evoshift.composeapp.generated.resources.privacy_choices_title
import evoshift.composeapp.generated.resources.privacy_consent_text
import evoshift.composeapp.generated.resources.privacy_consent_title
import evoshift.composeapp.generated.resources.privacy_crash_reports
import evoshift.composeapp.generated.resources.privacy_crash_reports_desc
import evoshift.composeapp.generated.resources.privacy_not_now
import evoshift.composeapp.generated.resources.privacy_usage_stats
import evoshift.composeapp.generated.resources.privacy_usage_stats_desc
import org.jetbrains.compose.resources.stringResource

// Crash reports and usage statistics (SH-86): the first-run question and the switches in Profile.
// Both off by default; the two answers of the question look alike (no nudge towards "allow").

/** Test tags of the two choices (sheet checkboxes and Profile switches). */
object PrivacyChoiceTags {
    const val CRASH_REPORTS = "privacy_crash_reports"
    const val USAGE_STATS = "privacy_usage_stats"
}

/**
 * The first-run question, once per device: two unticked boxes, "Allow selected" and "Not now" side
 * by side in the same style. Closing the sheet any other way counts as "Not now".
 */
@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun PrivacyConsentSheet(viewModel: PrivacyChoicesViewModel, onDone: () -> Unit) {
    var crashReports by rememberSaveable { mutableStateOf(false) }
    var usageStats by rememberSaveable { mutableStateOf(false) }
    val uri = LocalUriHandler.current
    val privacyUrl = stringResource(Res.string.legal_privacy_url)
    val notNow = { viewModel.notNow(); onDone() }

    EvoBottomSheet(
        onDismiss = notNow,
        title = stringResource(Res.string.privacy_consent_title),
        icon = Icons.Outlined.Insights
    ) {
        Text(
            stringResource(Res.string.privacy_consent_text),
            style = MaterialTheme.typography.bodyMedium,
            color = Color.White.copy(alpha = 0.85f)
        )
        if (viewModel.offersCrashReports) {
            ConsentCheckRow(
                title = stringResource(Res.string.privacy_crash_reports),
                description = stringResource(Res.string.privacy_crash_reports_desc),
                checked = crashReports,
                onCheckedChange = { crashReports = it },
                tag = PrivacyChoiceTags.CRASH_REPORTS
            )
        }
        ConsentCheckRow(
            title = stringResource(Res.string.privacy_usage_stats),
            description = stringResource(Res.string.privacy_usage_stats_desc),
            checked = usageStats,
            onCheckedChange = { usageStats = it },
            tag = PrivacyChoiceTags.USAGE_STATS
        )
        TextButton(onClick = { uri.openUri(privacyUrl) }) {
            Text(stringResource(Res.string.legal_privacy), color = DarkGold, style = MaterialTheme.typography.labelMedium)
        }
        Row(horizontalArrangement = Arrangement.spacedBy(12.dp), modifier = Modifier.fillMaxWidth()) {
            EvoSecondaryButton(DialogAction(stringResource(Res.string.privacy_not_now), notNow), Modifier.weight(1f))
            EvoSecondaryButton(
                DialogAction(stringResource(Res.string.privacy_allow_selected), {
                    viewModel.allowSelected(crashReports = crashReports, usageStats = usageStats)
                    onDone()
                }),
                Modifier.weight(1f)
            )
        }
    }
}

@Composable
private fun ConsentCheckRow(title: String, description: String, checked: Boolean, onCheckedChange: (Boolean) -> Unit, tag: String) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(12.dp))
            .background(Color.White.copy(alpha = 0.06f))
            .toggleable(value = checked, role = Role.Checkbox, onValueChange = onCheckedChange)
            .testTag(tag)
            .padding(horizontal = 8.dp, vertical = 8.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Checkbox(
            checked = checked,
            onCheckedChange = null,
            colors = CheckboxDefaults.colors(
                checkedColor = DarkGold,
                uncheckedColor = Color.White.copy(alpha = 0.7f),
                checkmarkColor = DeepNavy
            )
        )
        Spacer(Modifier.width(8.dp))
        Column(Modifier.weight(1f)) {
            Text(title, style = MaterialTheme.typography.bodyLarge, fontWeight = FontWeight.SemiBold, color = Color.White)
            Text(description, style = MaterialTheme.typography.bodySmall, color = Color.White.copy(alpha = 0.7f))
        }
    }
}

/** Profile: the same two choices as switches, changeable any time; off stops sending at once. */
@Composable
fun PrivacyChoicesCard(viewModel: PrivacyChoicesViewModel, modifier: Modifier = Modifier) {
    val consent by viewModel.state.collectAsState()
    Column(modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(8.dp)) {
        Text(
            stringResource(Res.string.privacy_choices_title),
            style = MaterialTheme.typography.titleMedium,
            fontWeight = FontWeight.Bold
        )
        if (viewModel.offersCrashReports) {
            ChoiceSwitchRow(
                title = stringResource(Res.string.privacy_crash_reports),
                description = stringResource(Res.string.privacy_crash_reports_desc),
                checked = consent.crashReports,
                onCheckedChange = viewModel::setCrashReports,
                tag = PrivacyChoiceTags.CRASH_REPORTS
            )
        }
        ChoiceSwitchRow(
            title = stringResource(Res.string.privacy_usage_stats),
            description = stringResource(Res.string.privacy_usage_stats_desc),
            checked = consent.usageStats,
            onCheckedChange = viewModel::setUsageStats,
            tag = PrivacyChoiceTags.USAGE_STATS
        )
        Text(
            stringResource(Res.string.privacy_choices_note),
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant
        )
    }
}

/** The whole row toggles (one tap target, read as one switch by screen readers). */
@Composable
private fun ChoiceSwitchRow(title: String, description: String, checked: Boolean, onCheckedChange: (Boolean) -> Unit, tag: String) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(12.dp))
            .background(MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.5f))
            .toggleable(value = checked, role = Role.Switch, onValueChange = onCheckedChange)
            .testTag(tag)
            .padding(horizontal = 16.dp, vertical = 12.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Column(Modifier.weight(1f)) {
            Text(title, style = MaterialTheme.typography.bodyLarge, fontWeight = FontWeight.Medium)
            Text(description, style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.onSurfaceVariant)
        }
        Spacer(Modifier.width(12.dp))
        Switch(checked = checked, onCheckedChange = null)
    }
}
